import { confirmActionModal } from "@helpers/ConfirmValidate";
import { errorHandler } from "@helpers/errorHandler";
import {
    Call,
    CallWithFormData,
    CallWithFormDataFile,
    cleanEmpty,
    objectToFormData,
} from "@helpers/fetch";
import {
    startUILoading,
    stopUILoading,
} from "@redux/actions/administrador/ui/ui";
import { createSlice } from "@reduxjs/toolkit";
import Swal from "sweetalert2";

const initialState = {
    active: null,
    activeDocUri: "",
    list: [],
    pagination: {
        data: [],
        per_page: 10,
        total: 0,
    },
    grouped: null,
    count: null,
    reviewsSummary: [],
    meta: null,
};

export const EventParticipants = createSlice({
    name: "EventParticipants",
    initialState,
    reducers: {
        setActive: (state, action) => {
            state.active = action.payload;
        },
        clearActive: (state) => {
            state.active = initialState.active;
        },
        setActiveDocUri: (state, action) => {
            state.activeDocUri = action.payload;
        },
        clearActiveDocUri: (state) => {
            state.activeDocUri = initialState.activeDocUri;
        },
        setList: (state, action) => {
            state.list = action.payload;
        },
        clearList: (state) => {
            state.list = initialState.list;
        },
        setGrouped: (state, action) => {
            state.grouped = action.payload;
        },
        clearGrouped: (state) => {
            state.grouped = initialState.grouped;
        },
        setPagination: (state, action) => {
            state.pagination = action.payload;
        },
        clearPagination: (state) => {
            state.pagination = initialState.pagination;
        },
        setReviewsSummary: (state, action) => {
            state.reviewsSummary = action.payload;
        },
        clearReviewsSummary: (state) => {
            state.reviewsSummary = initialState.reviewsSummary;
        },
        setMeta: (state, action) => {
            state.meta = action.payload;
        },
        clearMeta: (state) => {
            state.meta = initialState.meta;
        },
        setCount: (state, action) => {
            state.count = action.payload;
        },
        clearCount: (state) => {
            state.count = initialState.count;
        },
        clearState: (state) => {
            state = initialState;
        },
    },
});

export const {
    setActive,
    clearActive,

    setActiveDocUri,
    clearActiveDocUri,

    setList,
    clearList,

    setGrouped,
    clearGrouped,

    setPagination,
    clearPagination,

    setReviewsSummary,
    clearReviewsSummary,

    setMeta,
    clearMeta,

    setCount,
    clearCount,

    clearState,
} = EventParticipants.actions;

export default EventParticipants.reducer;

export const startRegister = (
    academic_event_id,
    values,
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        values = cleanEmpty({ ...values, academic_event_id });
        const formData = objectToFormData(values);

        const resp = await CallWithFormData(
            `participants/event-participants/register`,
            "POST",
            formData
        );

        if (resp.success) {
            Swal.fire({
                icon: "success",
                title: "Gracias por tu registro",
                text: "Pronto recibirás un correo con la confirmación al evento.",
                confirmButtonText: "Aceptar",
            });
            callback();
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};

export const startTrack = (
    { folio, password },
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        // Quitar espacios en blanco al inicio y final
        const cleanFolio = folio?.trim() ?? "";
        const cleanPassword = password?.trim() ?? "";

        const resp = await Call(
            `participants/event-participants/track`,
            "POST",
            { folio: cleanFolio, password: cleanPassword }
        );

        if (resp.success) {
            callback();
            dispatch(setActive(resp.success));
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};

export const startShow = (values) => {
    return async (dispatch) => {
        const {
            query,
            page,
            number_rows,

            academic_event_id,
            participation,
            activity_item_id,
            approval_status,
            assistance,
            created_at,

            count = false,
            grouped = null,
            passed_attendance = null,
        } = values;

        if (!count) dispatch(startUILoading());

        const params = new URLSearchParams();
        params.set("page", page);
        params.set("per_page", number_rows);
        params.set("q", query);
        params.set("academic_event_id", academic_event_id);

        if (participation) {
            const value = Array.isArray(participation)
                ? participation.map((item) => item.value).join(",")
                : participation;
            params.set("participation", value);
        }
        if (activity_item_id) {
            const value = Array.isArray(activity_item_id)
                ? activity_item_id.map((item) => item.value).join(",")
                : activity_item_id;
            params.set("activity_item_id", value);
        }
        if (approval_status) {
            const value = Array.isArray(approval_status)
                ? approval_status.map((item) => item.value).join(",")
                : approval_status;
            params.set("approval_status", value);
        }
        if (assistance) {
            const value = Array.isArray(assistance)
                ? assistance.map((item) => item.value).join(",")
                : assistance;
            params.set("assistance", value);
        }
        if (created_at?.from) {
            params.set("created_at_from", created_at?.from);
        }
        if (created_at?.to) {
            params.set("created_at_to", created_at?.to);
        }
        if (count) {
            params.set("count", 1);
        }
        if (grouped) {
            params.set("grouped", grouped);
        }
        if (passed_attendance !== null) {
            params.set("passed_attendance", passed_attendance ? 1 : 0);
        }

        const resp = await Call(
            "participants/event-participants",
            "GET",
            params.toString()
        );

        if (resp.success || resp.success === 0) {
            if (count) {
                dispatch(setCount(resp.success));
            } else if (!!grouped) {
                dispatch(setGrouped(resp.success));
            } else {
                dispatch(setPagination(resp.success));
                dispatch(setMeta(resp.meta));
            }
        } else {
            errorHandler(resp);
        }

        if (!count) dispatch(stopUILoading());
    };
};

export const startGetOne = (eventParticipantId) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            "participants/event-participants/" + eventParticipantId,
            "GET"
        );

        if (resp.success) {
            dispatch(setActive(resp.success));
        }

        dispatch(stopUILoading());
    };
};

export const startReview = (
    event_participant_id,
    values,
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        let confirmed = true;

        if (!!values.attachment && values.approval_status === "APROBADO") {
            const result = await Swal.fire({
                title: "Confirmar esta acción",
                text: "El archivo que ya estaba cargado será eliminado permanentemente. ¿Deseas continuar?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Sí, continuar",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#2e4a6c",
                cancelButtonColor: "#6c757d",
            });

            confirmed = result.isConfirmed;
        }

        if (!confirmed) {
            return;
        }
        dispatch(startUILoading());

        const resp = await Call(
            `participants/event-participants/review/${event_participant_id}`,
            "PATCH",
            values
        );

        if (resp.success) {
            Swal.fire({
                toast: true,
                position: "bottom-end",
                icon: "success",
                title: "Asistencia revisada correctamente",
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            });

            callback();
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};

export const startResetPassword = (
    folio,
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        await confirmActionModal(
            async () => {
                dispatch(startUILoading());

                const resp = await Call(
                    "participants/event-participants/reset-password",
                    "PATCH",
                    { folio }
                );

                if (resp.success) {
                    Swal.fire({
                        toast: true,
                        position: "bottom-end",
                        icon: "success",
                        title: "Contraseña reestablecida correctamente",
                        showConfirmButton: false,
                        timer: 5000,
                        timerProgressBar: true,
                    });

                    callback();
                } else {
                    errorHandler(resp, setErrors);
                }

                dispatch(stopUILoading());
            },
            {
                title: "¿Deseas reestablecer la contraseña?",
                text: `Esta acción generará una nueva contraseña para el folio ${folio}, y se enviará por correo.`,
                confirmText: "Continuar",
            }
        );
    };
};

export const startGetDocumentUri = (values) => {
    return async (dispatch) => {
        // dispatch(startUILoading());

        const resp = await Call(
            "participants/event-participants/get-document-uri",
            "POST",
            values
        );

        if (resp.success) {
            dispatch(setActiveDocUri(resp.success.uri));
        } else {
            errorHandler(resp);
        }

        // dispatch(stopUILoading());
    };
};

export const startTeacherReview = (
    event_participant_id,
    values,
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            "participants/teachers/review/" + event_participant_id,
            "POST",
            values
        );

        if (resp.success) {
            Swal.fire({
                toast: true,
                position: "bottom-end",
                icon: "success",
                title: "Revision generada correctamente",
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            });

            callback();
        } else {
            errorHandler(resp);
        }

        dispatch(stopUILoading());
    };
};

export const startGetReviewsSummary = (academic_event_id) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            "operators/academic-events/reports/reviews-summary/" +
                academic_event_id,
            "GET"
        );

        if (resp.success) {
            dispatch(setReviewsSummary(resp.success));
        }

        dispatch(stopUILoading());
    };
};

export const startUpdateAttachment = (
    eventParticipantId,
    type,
    attachment,
    setErrors = () => {},
    callback = () => {},
    showSwal = true
) => {
    return async (dispatch) => {
        let result = null;
        let actionType = "upload"; // por defecto subir

        if (typeof attachment === "string" && attachment === "remove") {
            actionType = "remove";
        }

        if (showSwal) {
            if (actionType === "remove") {
                // Confirmación para eliminar
                result = await Swal.fire({
                    title: "¿Eliminar documento?",
                    text: "Esta acción eliminará el archivo adjunto. ¿Deseas continuar?",
                    icon: "warning",
                    showCancelButton: true,
                    cancelButtonText: "Cancelar",
                    confirmButtonText: "Sí, eliminar",
                    confirmButtonColor: "#d33",
                    cancelButtonColor: "#6c757d",
                });
            } else {
                // Confirmación para subir
                result = await Swal.fire({
                    title: "Confirmar esta acción",
                    icon: "info",
                    text: "Confirmo que el archivo que estoy a punto de subir es correcto.",
                    showCancelButton: true,
                    cancelButtonText: "Cancelar",
                    confirmButtonText: "Sí, confirmo",
                    confirmButtonColor: "#2e4a6c",
                    cancelButtonColor: "#6c757d",
                });
            }
        }

        if (result?.isConfirmed || !showSwal) {
            dispatch(startUILoading());

            const resp = await CallWithFormData(
                `participants/event-participants/update-attachment/${eventParticipantId}`,
                "POST",
                objectToFormData({ type, attachment })
            );

            if (resp.success) {
                Swal.fire({
                    title: "Éxito",
                    text:
                        actionType === "remove"
                            ? "El archivo fue eliminado."
                            : "El archivo fue cargado correctamente.",
                    icon: "success",
                    toast: true,
                    position: "bottom-end",
                    timer: 8000,
                });
                callback();
                dispatch(setActive(resp.success));
            } else {
                errorHandler(resp, setErrors);
            }

            dispatch(stopUILoading());
        }
    };
};

export const startNotifyParticipants = (
    data,
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        const { test } = data;

        if (!test) {
            const result = await Swal.fire({
                icon: "warning",
                title: "Confirmar envío masivo",
                text: "¿Estás seguro de enviar esta notificación a los participantes?",
                showCancelButton: true,
                confirmButtonText: "Sí, enviar",
                cancelButtonText: "Cancelar",
            });

            if (!result.isConfirmed) {
                return;
            }
        }

        dispatch(startUILoading());

        const resp = await Call(
            "operators/academic-events/notify/participants",
            "POST",
            data
        );

        if (resp.success) {
            Swal.fire({
                icon: "success",
                title: "Éxito",
                text: "Notificación enviada satisfactoriamente.",
                confirmButtonText: "Aceptar",
            });
            callback();
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};

export const startImportAttendances = (data, callback = () => {}) => {
    return async (dispatch) => {
        const { academic_event_id, files, attendance_override } = data;

        // Confirmación antes de importar
        const result = await Swal.fire({
            title: "Confirmar importación",
            html: `Está a punto de importar <strong>${
                files.length
            }</strong> archivo${files.length > 1 ? "s" : ""}.<br>
                   Si tienes varios reportes, deben importarse todos al mismo tiempo.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Continuar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#2e4a6c",
            cancelButtonColor: "#6c757d",
            width: 500,
        });

        if (!result.isConfirmed) return; // Si cancela, no hace nada

        dispatch(startUILoading());

        const formData = new FormData();
        formData.append("academic_event_id", academic_event_id);
        [...files].forEach((f) => formData.append("files[]", f));
        formData.append("attendance_override", attendance_override ? 1 : 0);

        const resp = await CallWithFormData(
            `operators/academic-events/import-attendances/${academic_event_id}`,
            "POST",
            formData
        );

        if (resp.success) {
            // Construir resumen HTML
            const { file_map, count, failed } = resp.success;
            let htmlSummary = "";

            file_map.forEach((fileName, index) => {
                const successCount = count[index]?.success ?? 0;
                const failedCount = count[index]?.failed ?? 0;
                const failedFolios = failed[index]
                    ? Object.keys(failed[index])
                    : [];

                // Construir los spans para folios fallidos
                const failedListHtml = failedFolios.length
                    ? failedFolios
                          .map(
                              (folio) =>
                                  `<span style="white-space: nowrap;">${folio}</span>`
                          )
                          .join(", ")
                    : "Ninguno";

                htmlSummary += `
                    <div style="margin-bottom:10px;text-align:start;">
                        <strong>Archivo:</strong> ${fileName}<br>
                        <strong style="color:green">Folios exitosos:</strong> ${successCount}<br>
                        <strong style="color:red">Folios fallidos:</strong> ${failedCount} <br>
                        <span style="font-size:12px; color:gray;">${failedListHtml}</span>
                    </div>
                `;
            });

            Swal.fire({
                icon: "success",
                title: "Importación completada",
                html: htmlSummary,
                width: 800,
                confirmButtonText: "Aceptar",
            });

            callback();
        } else {
            errorHandler(resp);
        }

        dispatch(stopUILoading());
    };
};
